import React from 'react';
import PropTypes from 'prop-types';
import { StyledGroup, StyledControl } from './styled';
import { trimLowerCase } from '../../../helpers/shared';
import { Error } from '../simple/Error';
import { Label } from '../simple/Label';
import { TooltipIcon } from '../simple/TooltipIcon';
import { useInput } from '../../../hooks/useInput';
import imgEyeClosed from '../../../assets/images/profile/eye-closed.png';
import imgEyeOpen from '../../../assets/images/profile/eye-open.png';
import { useSwitchDesign } from '../../../hooks/useSwitchDesign';
import { theme } from '../../../theme/theme';
import styled from 'styled-components';
import { Required } from './styled';

/**
 * Input component
 * @component
 * @param {String} autoComplete
 * @param {String} className
 * @param {Boolean} disabled
 * @param {Object} field
 * @param {String} id
 * @param {Boolean} isRequiredInput
 * @param {String} label
 * @param {String} message
 * @param {String} name
 * @param {String} placeholder
 * @param {Object} register
 * @param {Function} setValue
 * @param {String} tooltipText
 * @param {String} type
 * @param {String} labelColor     - String for a custom color for the Input's label, either by name or hex value.
 * @param {Object} altErrorStyle  - Object for applyin cuustom styles for error message
 * @returns
 */


export const Input = ({
  autoComplete,
  className,
  disabled,
  field = {},
  id,
  isRequiredInput,
  label,
  message,
  name,
  placeholder,
  register,
  setValue = () => {},
  tooltipText,
  type,
  maskLength,
  validate,
  entryProps,
  onIconClick,
  labelColor = '',
  isNewDesign = false,
  isDocument = false, 
  documentType = ''
}) => {
  const { fieldAction, onClick, onKeyDown, onKeyUp } = useInput(
    field, type, setValue, entryProps);
  
  const { altErrorStyle } = useSwitchDesign()
  const { third } = theme


  return (
    <StyledGroup className={`mb-3 ${className}`}>
      <Label htmlFor={ name || id } color={labelColor} isNewDesign={isNewDesign}>
        { label }
        
        { // ? For differentiating documents
          isDocument && (
            (<TooltipIcon tooltipText={ documentType?.description } color={isNewDesign ? third : undefined} />)
          )
        }
        
        {isRequiredInput && (
          <Required className="text-danger">&nbsp;*</Required>)}
        {tooltipText && (<TooltipIcon tooltipText={ tooltipText } color={isNewDesign ? third : undefined} />)}
      </Label>
      <StyledControl
        { ...register }
        as={(trimLowerCase(type) === 'textarea') ? 'textarea' : ''}
        autoComplete={ autoComplete }
        className={ className }
        $isNewDesign={ isNewDesign }
        data-action={ field?.action }
        data-category={ (field?.category) && `category-${field.category.field_category}` }
        data-condition={ field?.parent_condition }
        data-parent={ field?.parent }
        data-unique-name={ field?.unique_name }
        data-min-length={maskLength ? maskLength : null}
        data-validate={validate}
        disabled={ disabled }
        id={ id }
        key={ id }
        maxLength={ (field?.max_length) && field.max_length?.toString() }
        onKeyDown={ (event) => (type === 'number') ? onKeyDown(event) : null }
        onKeyUp={ (event) => (fieldAction?.validate) ? onKeyUp(event) : null }
        placeholder={ placeholder }
        type={ type }
      />
      {type === 'password' && (
        <span
          className="field-icon-toggle"
          onClick={ event => onClick(event, name) }
        >
          <img
            alt="Eye Open"
            className="toggle-password img-eye-open"
            data-password={ name }
            src={ imgEyeOpen }
          />
          <img
            alt="Eye Closed"
            className="toggle-password d-none img-eye-closed"
            data-password={ name }
            src={ imgEyeClosed }
          />
        </span>
      )}

      {(field?.data_type === 'partially_hidden' && type !== 'password') && (
        <span
          className={`field-icon-toggle ${className}`}
          onClick={ onIconClick }
        >
          <img
            alt="Eye Closed"
            className="toggle-password img-eye-closed"
            data-password={ name }
            src={ imgEyeClosed }
          />
        </span>
      )}

      {message && (
        <Error 
          message={ message } 
          altErrorMessageStyle={altErrorStyle}
        />
      )}
    </StyledGroup>
  );
};

Input.propTypes = {
  autoComplete: PropTypes.string,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  field: PropTypes.object,
  id: PropTypes.string,
  isRequiredInput: PropTypes.bool,
  label: PropTypes.string.isRequired,
  message: PropTypes.string,
  name: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  setValue: PropTypes.func,
  register: PropTypes.object.isRequired,
  tooltipText: PropTypes.string,
  type: PropTypes.string.isRequired
};
